<template>
  <div class="choose-page">
    <header class="choose-head">
      <button class="head-btn" type="button" :aria-label="$t('auth.back')" @click="back">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4">
          <path d="M15 18l-6-6 6-6"></path>
        </svg>
      </button>

      <div class="head-copy">
        <h1>{{ copy.crushTitle }}</h1>
      </div>

      <button class="head-btn" type="button" :aria-label="$t('auth.home')" @click="goHome">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M3 11l9-8 9 8"></path>
          <path d="M5 10v10h14V10"></path>
        </svg>
      </button>
    </header>

    <main class="choose-body">
      <div class="tabs">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="tab"
          :class="{ on: activeTab === tab.key }"
          type="button"
          @click="activeTab = tab.key"
        >
          {{ tab.label }}
        </button>
      </div>

      <div v-if="cardsToShow.length" class="grid">
        <button
          v-for="item in cardsToShow"
          :key="`choose-${item.id}`"
          class="card"
          type="button"
          @click="goProfile(item)"
        >
          <div class="cover">
            <img :src="item.image" :alt="item.name" />
            <div class="card-tag">
              <span class="dot" :class="{ pink: !item.online }"></span>
              {{ item.tag }}
            </div>
            <div v-if="item.certified" class="card-ok">√ {{ certifiedLabel }}</div>
            <div class="card-topline">
              <span>{{ item.name }}</span>
              <span
                class="card-heart"
                :class="{ on: favoriteIds.includes(Number(item.id)) }"
                @click.stop="toggleFavorite(item)"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 21s-7-4.35-7-10a4 4 0 0 1 7-2.65A4 4 0 0 1 19 11c0 5.65-7 10-7 10z"></path>
                </svg>
              </span>
            </div>
          </div>

          <div class="card-info">
            <div class="card-data">
              <span>{{ copy.identity }}</span>
              <b>{{ item.role }}</b>
            </div>
            <div class="card-data">
              <span>{{ copy.city }}</span>
              <b>{{ item.city }}</b>
            </div>
            <div class="card-data">
              <span>{{ copy.profileId }}</span>
              <b>{{ item.publicId || "--" }}</b>
            </div>
          </div>
        </button>
      </div>

      <div v-else-if="!loading" class="empty-state">
        <strong>{{ $t("No information yet") }}</strong>
        <button class="back-home" type="button" @click="goHome">{{ $t("auth.home") }}</button>
      </div>

      <button class="more" type="button" @click="openViewMore">
        {{ copy.viewMore || $t("index.more") }}
      </button>
    </main>
  </div>
</template>

<script>
import { getListPayload } from "@/common/apiData";
import { getPageCopy } from "@/common/pageLocale";
import { resolveAssetUrl } from "@/common/runtimeConfig";
import { openKefuContact } from "@/common/kefu";
import { buildAddressMap, normalizeXuanfeiProfile } from "@/common/xuanfeiProfile";

const PAGE_SIZE = 100;

export default {
  data() {
    return {
      profiles: [],
      addressMap: {},
      activeTab: "recommend",
      favoriteIds: [],
      page: 1,
      hasMore: false,
      loading: false,
    };
  },
  computed: {
    copy() {
      return getPageCopy(this, "home");
    },
    ui() {
      const baseInfo = this.$store.getters.getBaseInfo || {};
      return baseInfo.ui || {};
    },
    certifiedLabel() {
      const raw = this.ui.certified_label;
      if (raw != null && String(raw).trim()) {
        return String(raw).trim();
      }
      return this.copy.certified || "已完善资料";
    },
    tabs() {
      return [
        { key: "recommend", label: this.copy.tabs && this.copy.tabs.recommend },
        { key: "nearby", label: this.copy.tabs && this.copy.tabs.nearby },
        { key: "online", label: this.copy.tabs && this.copy.tabs.online },
        { key: "newcomer", label: this.copy.tabs && this.copy.tabs.newcomer },
      ];
    },
    cardsToShow() {
      let list = this.profiles;
      if (this.activeTab === "recommend") {
        list = this.profiles.filter((item) => item.hot);
      } else if (this.activeTab === "nearby") {
        list = this.profiles.filter((item) => item.group === "nearby");
      } else if (this.activeTab === "online") {
        list = this.profiles.filter((item) => item.online);
      } else if (this.activeTab === "newcomer") {
        list = this.profiles.filter((item) => item.isNew);
      }
      return list.length ? list : this.profiles;
    },
  },
  created() {
    this.fetchAddressMap().then(() => {
      this.fetchProfiles(true);
    });
    this.fetchFavoriteIds();
  },
  methods: {
    normalizeProfile(item, index) {
      return normalizeXuanfeiProfile(item, index, {
        copy: this.copy,
        addressMap: this.addressMap,
        lang: this.$i18n.locale || "zh_cn",
        resolveAssetUrl,
      });
    },
    async fetchAddressMap() {
      try {
        const res = await this.$http({
          method: "get",
          url: "address_list",
        });
        const list = Array.isArray(res && res.data) ? res.data : getListPayload(res && res.data);
        this.addressMap = buildAddressMap(list, this.$i18n.locale || "zh_cn");
      } catch (error) {
        this.addressMap = {};
      }
    },
    async fetchProfiles(reset) {
      if (this.loading) {
        return;
      }
      const nextPage = reset ? 1 : this.page + 1;
      this.loading = true;
      try {
        if (reset && !Object.keys(this.addressMap).length) {
          await this.fetchAddressMap();
        }
        const res = await this.$http({
          method: "get",
          url: "xuanfeilist",
          data: { page: nextPage, limit: PAGE_SIZE },
        });
        const rows = getListPayload(res && res.data);
        const offset = reset ? 0 : this.profiles.length;
        const mapped = rows.map((item, index) => this.normalizeProfile(item, offset + index));
        this.profiles = reset ? mapped : this.profiles.concat(mapped);
        this.page = nextPage;
        this.hasMore = rows.length >= PAGE_SIZE;
        if (reset) {
          this.activeTab = this.profiles.some((item) => item.hot) ? "recommend" : "online";
        }
      } catch (error) {
        if (reset) {
          this.profiles = [];
          this.hasMore = false;
        }
      } finally {
        this.loading = false;
      }
    },
    loadMore() {
      this.fetchProfiles(false);
    },
    openViewMore() {
      openKefuContact(this);
    },
    async fetchFavoriteIds() {
      if (!localStorage.getItem("token")) {
        this.favoriteIds = [];
        return;
      }
      try {
        const res = await this.$http({
          method: "get",
          url: "xuanfei_favorite_ids",
        });
        const ids = Array.isArray(res && res.data) ? res.data : [];
        this.favoriteIds = ids.map((item) => Number(item)).filter((item) => Number.isFinite(item));
      } catch (error) {
        this.favoriteIds = [];
      }
    },
    goProfile(profile) {
      this.$router.push({
        path: "/Profile",
        query: {
          id: profile.id,
          name: profile.name,
          role: profile.role,
          city: profile.city,
          publicId: profile.publicId,
          tag: profile.tag,
          image: profile.image,
          about: profile.about,
        },
      });
    },
    async toggleFavorite(profile) {
      if (!localStorage.getItem("token")) {
        this.$toast(this.copy.loginFirst);
        this.$router.push("/Login");
        return;
      }
      const res = await this.$http({
        method: "post",
        url: "xuanfei_favorite_toggle",
        data: {
          id: profile.id,
          name: profile.name,
          role: profile.role,
          city: profile.city,
          public_id: profile.publicId,
          image: profile.image,
          about: profile.about,
        },
      });
      if (!res) {
        return;
      }
      if (res.code === 200) {
        const id = Number(profile.id);
        const liked = !!(res.data && res.data.liked);
        this.favoriteIds = liked
          ? this.favoriteIds.concat(this.favoriteIds.includes(id) ? [] : [id])
          : this.favoriteIds.filter((item) => item !== id);
        this.$toast(
          liked
            ? this.copy.favoriteSuccess || "心动收藏成功"
            : this.copy.favoriteRemoved || "已取消心动"
        );
        return;
      }
      if (res.msg) {
        this.$toast(res.msg);
      }
    },
    back() {
      if (window.history.length > 1) {
        this.$router.back();
        return;
      }
      this.goHome();
    },
    goHome() {
      this.$router.replace("/Home");
    },
  },
};
</script>

<style lang="less" scoped>
.choose-page {
  width: 100%;
  max-width: 23.4375rem;
  min-height: 100vh;
  min-height: 100dvh;
  margin: 0 auto;
  padding-bottom: 8.5rem;
  background:
    radial-gradient(circle at 0 0, rgba(165, 92, 255, 0.13), transparent 28%),
    radial-gradient(circle at 100% 8%, rgba(255, 63, 120, 0.12), transparent 24%),
    #0a0710;
  color: #fff7fb;
}

.choose-head {
  position: sticky;
  top: 0;
  z-index: 20;
  display: grid;
  grid-template-columns: 2.625rem 1fr 2.625rem;
  align-items: center;
  gap: 0.5rem;
  min-height: 4.1875rem;
  padding: max(0.5rem, env(safe-area-inset-top)) 0.75rem 0.5rem;
  background: rgba(10, 7, 16, 0.92);
  border-bottom: 1px solid rgba(255, 255, 255, 0.085);
  backdrop-filter: blur(1.125rem);
  -webkit-backdrop-filter: blur(1.125rem);
}

.head-btn {
  width: 2.375rem;
  height: 2.375rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.045);
  color: #fff;
}

.head-btn svg {
  width: 1.15rem;
  height: 1.15rem;
}

.head-copy h1 {
  margin: 0;
  text-align: center;
  font-size: 1.2rem;
  font-weight: 900;
  letter-spacing: -0.02rem;
}

.head-copy p {
  margin: 0.2rem 0 0;
  text-align: center;
  color: #a89baa;
  font-size: 0.72rem;
}

.choose-body {
  padding: 0.85rem 0.75rem 1.25rem;
}

.tabs {
  display: flex;
  gap: 0.5rem;
  margin: 0 -0.75rem 0.9rem;
  padding: 0 0.75rem;
  overflow-x: auto;
  scrollbar-width: none;
}

.tabs::-webkit-scrollbar {
  display: none;
}

.tab {
  flex: 0 0 auto;
  height: 2.1rem;
  padding: 0 0.88rem;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 1.05rem;
  background: rgba(255, 255, 255, 0.04);
  color: #bfb2bd;
  font-size: 0.77rem;
  font-weight: 800;
  white-space: nowrap;
}

.tab.on {
  color: #ff91b1;
  border-color: rgba(255, 77, 133, 0.55);
  background: rgba(255, 63, 120, 0.14);
}

.grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.card {
  overflow: hidden;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 1.35rem;
  background: #19121f;
  color: #fff7fb;
  text-align: left;
  box-shadow: 0 0.8rem 1.8rem rgba(0, 0, 0, 0.18);
}

.cover {
  position: relative;
  height: 13rem;
  overflow: hidden;
  background: #24182d;
}

.cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cover::after {
  content: "";
  position: absolute;
  inset: 35% 0 0;
  background: linear-gradient(180deg, transparent, rgba(8, 5, 11, 0.86));
}

.card-tag,
.card-ok {
  position: absolute;
  top: 0.65rem;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 1.55rem;
  padding: 0 0.55rem;
  border-radius: 0.78rem;
  font-size: 0.62rem;
  font-weight: 800;
}

.card-tag {
  left: 0.55rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(10, 7, 16, 0.6);
}

.card-ok {
  right: 0.55rem;
  background: rgba(255, 63, 120, 0.82);
}

.dot {
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 50%;
  background: #4de3a5;
  box-shadow: 0 0 0 0.32rem rgba(77, 227, 165, 0.12);
}

.dot.pink {
  background: #ff3f78;
  box-shadow: 0 0 0 0.32rem rgba(255, 63, 120, 0.16);
}

.card-topline {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.65rem 0.65rem 0.55rem;
  color: #fff;
  font-size: 0.92rem;
  font-weight: 800;
}

.card-heart {
  width: 1.95rem;
  height: 1.95rem;
  flex: 0 0 auto;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.14);
}

.card-heart svg {
  width: 1rem;
  height: 1rem;
}

.card-heart.on {
  background: #ff3f78;
}

.card-info {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.7rem 0.75rem 0.8rem;
}

.card-data {
  display: grid;
  grid-template-columns: 2.7rem minmax(0, 1fr);
  gap: 0.4rem;
}

.card-data span {
  color: #887b87;
  font-size: 0.58rem;
  white-space: nowrap;
}

.card-data b {
  color: #e7dce4;
  font-size: 0.68rem;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-data:first-child b {
  color: #fff;
  font-size: 0.74rem;
  font-weight: 800;
}

.empty-state {
  display: flex;
  min-height: 16rem;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #b8aab7;
  text-align: center;
}

.back-home,
.more {
  height: 2.65rem;
  margin-top: 1rem;
  border: 0;
  border-radius: 1.35rem;
  background: linear-gradient(135deg, #ff6d9c, #ff3f78);
  color: #fff;
  font-size: 0.88rem;
  font-weight: 900;
  box-shadow: 0 0.65rem 1.6rem rgba(255, 63, 120, 0.3);
}

.back-home {
  min-width: 7.5rem;
  padding: 0 1.2rem;
}

.more {
  width: 100%;
}

.more:disabled {
  opacity: 0.6;
}

button {
  cursor: pointer;
}

@media (max-width: 47.9375rem) {
  .choose-page {
    max-width: 24.25rem;
  }

  .cover {
    height: 12.4rem;
  }
}

@media (min-width: 48rem) {
  .choose-page {
    max-width: 25rem;
  }
}
</style>
